import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, Flame, Pencil, Timer, Trash2 } from "lucide-react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { ABtn, Panel } from "./AdminLayout";
import { useCountdown } from "@/components/deals";

const pad = (n) => String(n).padStart(2, "0");

const Remaining = ({ target }) => {
  const { d, h, m, s, done } = useCountdown(target);
  if (done) return <span className="font-num font-semibold text-rose-400">Expirée</span>;
  return (
    <span className="font-num font-semibold text-emerald-400">
      {d > 0 ? `${d}j ` : ""}
      {pad(h)}:{pad(m)}:{pad(s)}
    </span>
  );
};

const Kpi = ({ label, value, icon: Icon, tone = "brand" }) => (
  <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4" data-testid={`deal-kpi-${label}`}>
    <div className="flex items-start justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <Icon className={`h-4 w-4 ${tone === "rose" ? "text-rose-400" : tone === "gold" ? "text-amber-400" : "text-emerald-400"}`} />
    </div>
    <p className="font-num mt-3 text-xl font-bold text-white">{value}</p>
  </div>
);

const discount = (d) => (d.compare_price > d.price ? Math.round((1 - d.price / d.compare_price) * 100) : 0);

export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = () =>
    api
      .get("/admin/deals")
      .then((r) => setDeals(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const now = Date.now();
  const active = deals.filter((d) => new Date(d.deal_ends_at).getTime() > now);
  const expired = deals.length - active.length;
  const bestPct = active.reduce((m, d) => Math.max(m, discount(d)), 0);

  const remove = async (d) => {
    if (!window.confirm(`Retirer l'offre du jour sur « ${d.name} » ?`)) return;
    try {
      await api.delete(`/admin/products/${d.id}/deal`);
      toast.success("Offre retirée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runAlerts = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/deal-alerts/run");
      toast.success(`${data.alerts} alerte(s) de fin d'offre envoyée(s)`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-deals-page">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Offres en cours" value={active.length} icon={Flame} tone="rose" />
        <Kpi label="Offres expirées" value={expired} icon={Timer} />
        <Kpi label="Meilleure remise" value={`${bestPct}%`} icon={Flame} tone="gold" />
        <Kpi label="Total configurées" value={deals.length} icon={Timer} />
      </div>

      <Panel
        title="Offres du jour"
        subtitle="Suivez en direct le temps restant de chaque promotion"
        actions={
          <ABtn variant="gold" onClick={runAlerts} disabled={sending} data-testid="admin-deals-run-alerts">
            <BellRing className="h-4 w-4" /> {sending ? "Envoi…" : "Relancer les paniers"}
          </ABtn>
        }
        testid="admin-deals-panel"
      >
        {loading ? (
          <p className="text-sm text-slate-400" data-testid="admin-deals-loading">Chargement…</p>
        ) : deals.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center" data-testid="admin-deals-empty">
            <Flame className="mx-auto h-8 w-8 text-rose-400" />
            <p className="mt-2 text-sm font-semibold text-white">Aucune offre du jour configurée</p>
            <p className="mt-1 text-xs text-slate-400">
              Ajoutez une « Fin de l'offre du jour » + un « Ancien prix » sur une fiche produit, ou via l'import Excel.
            </p>
            <ABtn className="mt-4" onClick={() => navigate("/admin/produits")} data-testid="admin-deals-goto-products">
              <Pencil className="h-4 w-4" /> Gérer les produits
            </ABtn>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-400">
                <tr className="border-b border-slate-800">
                  <th className="py-2">Produit</th>
                  <th className="py-2 text-right">Remise</th>
                  <th className="py-2 text-right">Prix offre</th>
                  <th className="py-2 text-right">Stock</th>
                  <th className="py-2 text-right">Temps restant</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => {
                  const pct = discount(d);
                  const isActive = new Date(d.deal_ends_at).getTime() > now;
                  return (
                    <tr key={d.id} className="border-b border-slate-800/60 hover:bg-slate-800/40" data-testid={`admin-deal-row-${d.sku || d.id}`}>
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-slate-800">
                            {(d.images || [])[0] && <img src={mediaUrl(d.images[0])} alt="" className="h-full w-full object-cover" />}
                          </div>
                          <div>
                            <p className="line-clamp-1 font-medium text-white">{d.name}</p>
                            <p className="font-num text-[11px] text-slate-400">{d.sku}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 text-right">
                        {pct > 0 ? <span className="font-num font-bold text-rose-400">−{pct}%</span> : <span className="text-slate-500">—</span>}
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-num font-semibold text-emerald-400">{fmtGNF(d.price)}</span>
                        {d.compare_price > d.price && (
                          <span className="font-num ml-1 block text-[11px] text-slate-500 line-through">{fmtGNF(d.compare_price)}</span>
                        )}
                      </td>
                      <td className="font-num py-2 text-right text-slate-200">{d.stock}</td>
                      <td className="py-2 text-right"><Remaining target={d.deal_ends_at} /></td>
                      <td className="py-2">
                        {isActive ? (
                          <span className="ds-chip border-emerald-800 bg-emerald-950 text-emerald-300">En cours</span>
                        ) : (
                          <span className="ds-chip border-slate-700 bg-slate-800 text-slate-400">Expirée</span>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex justify-end gap-1">
                          <ABtn variant="ghost" onClick={() => navigate("/admin/produits")} data-testid={`admin-deal-edit-${d.sku || d.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </ABtn>
                          <ABtn variant="danger" onClick={() => remove(d)} data-testid={`admin-deal-remove-${d.sku || d.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </ABtn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
